import React, { useContext, useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Accordion, AccordionTab } from "primereact/accordion";
import * as Yup from "yup";
import Input from "../../../../components/form/input";
import DatePicker from "../../../../components/form/DatePicker";
import { formatErrors, handleSubmit } from "../../../../services/utils/processors";
import { ToastContext } from "../../../../services/contexts/toast-context";
import { FormWrapper, FormSection, GridRow, AccordionHeader, AddButtonRow, ActionRow } from "../form.styles";

function formatDateFields(items) {
  return items.map(item => ({
    ...item,
    start_date: item.start_date ? (String(item.start_date).length === 4 ? new Date(`${item.start_date}-01-01`) : new Date(item.start_date)) : null,
    end_date: item.end_date ? (String(item.end_date).length === 4 ? new Date(`${item.end_date}-12-31`) : new Date(item.end_date)) : null,
  }));
}

const persistedFields = ['name', 'university', 'degree', 'field_of_study', 'country', 'start_date', 'end_date'];

function serializeValue(value) {
  return value instanceof Date ? value.toISOString() : value ?? null;
}

function getEducationPayload(current, original) {
  const changed = { id: current.id };

  persistedFields.forEach((field) => {
    if (!original || serializeValue(current[field]) !== serializeValue(original[field])) {
      changed[field] = current[field] ?? null;
    }
  });

  return changed;
}

function checkIfDataChanged(originalData, currentData) {
  const serializedOriginal = JSON.stringify(originalData.map(item => persistedFields.reduce((result, field) => ({
    ...result,
    [field]: serializeValue(item[field]),
  }), { id: item.id })));
  const serializedCurrent = JSON.stringify(currentData.map(item => persistedFields.reduce((result, field) => ({
    ...result,
    [field]: serializeValue(item[field]),
  }), { id: item.id })));
  return serializedOriginal !== serializedCurrent;
}

const Education = ({ dbData = null, loading = false, onSave, onBack }) => {
  const toastRef = useContext(ToastContext);
  const baselineData = Array.isArray(dbData) ? dbData : [];
  const baselineDataKey = JSON.stringify(baselineData);
  const [educations, setEducations] = useState([]);
  const [initialEducations, setInitialEducations] = useState([]);
  const [educationError, setEducationError] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [isDirty, setIsDirty] = useState(false);

  const schema = Yup.array().of(
    Yup.object({
      name: Yup.string().required("Degree Name is required"),
      university: Yup.string().required("University is required"),
      degree: Yup.string().required("Degree is required"),
      field_of_study: Yup.string().required("Field of study is required"),
      country: Yup.string().nullable(),
      start_date: Yup.date().nullable().typeError("Start Date must be valid"),
      end_date: Yup.date().nullable(),
    })
  );

  useEffect(() => {
    const formatted = formatDateFields(baselineData);
    const formattedBaseline = formatDateFields(baselineData);
    setEducations(formatted);
    setInitialEducations(JSON.parse(JSON.stringify(formattedBaseline)));
    setEducationError({});
    setActiveTab(0);
  }, [baselineDataKey]);

  useEffect(() => {
    setIsDirty(checkIfDataChanged(initialEducations, educations));
  }, [educations, initialEducations]);

  const handleInputChange = (value, index, field) => {
    const updated = [...educations];
    updated[index] = { ...updated[index], [field]: value };
    setEducations(updated);
  };

  const addEducation = () => {
    setEducations([...educations, { name: "", start_date: null, end_date: null, university: "", degree: "", field_of_study: "", country: "" }]);
    setActiveTab(educations.length);
  };

  const removeEducation = (index) => {
    setEducations(educations.filter((_, i) => i !== index));
  };

  const handleSubmitData = async () => {
    const deletedIds = initialEducations
      .filter((education) => education.id && !educations.some((current) => current.id === education.id))
      .map((education) => education.id);
    const hasErrors = await handleSubmit(educations, schema, setEducationError, () => {
      const initialById = new Map(initialEducations.filter((education) => education.id).map((education) => [education.id, education]));
      const items = educations
        .map((education) => getEducationPayload(education, initialById.get(education.id)))
        .filter((education) => !education.id || Object.keys(education).length > 1);
      onSave({ items, deletedIds });
    });
    if (hasErrors && Object.entries(hasErrors).length !== 0) {
      // Find the first index with errors
      const firstErrorIndex = Object.keys(hasErrors).map(Number).sort()[0];
      setActiveTab(firstErrorIndex);
      toastRef.current.show({ severity: 'error', summary: "Error", detail: formatErrors(hasErrors), life: 6000 });
    }
  };

  return (
    <FormWrapper>
      <AddButtonRow>
        <Button type="button" label="Add Education" icon="pi pi-plus" className="p-button-success" onClick={addEducation} />
      </AddButtonRow>

      <Accordion activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)} style={{ width: "100%" }}>
        {educations.map((edu, index) => (
          <AccordionTab key={index}
            header={
              <AccordionHeader>
                <span>{edu.name || `Education ${index + 1}`}</span>
                <Button type="button" icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text"
                  onClick={(event) => { event.stopPropagation(); removeEducation(index); }} tooltip="Remove" tooltipOptions={{ position: "top" }} />
              </AccordionHeader>
            }
            style={{ minWidth: "50vw", backgroundColor: "transparent", border: "none", marginBottom: 16 }}
          >
            <FormSection>
              <GridRow>
                <Input value={edu.name} label="Program / Certificate Name" width="100%"
                  onChange={(v) => handleInputChange(v, index, "name")} error={educationError?.[`${index}`]?.name} />
                <Input value={edu.university} label="University / Institution" width="100%"
                  onChange={(v) => handleInputChange(v, index, "university")} error={educationError?.[`${index}`]?.university} />
              </GridRow>
              <GridRow>
                <DatePicker value={edu.start_date} label="Start Date" width="100%"
                  onChange={(v) => handleInputChange(v, index, "start_date")} error={educationError?.[`${index}`]?.start_date} />
                <DatePicker value={edu.end_date} label="End Date" width="100%"
                  onChange={(v) => handleInputChange(v, index, "end_date")} error={educationError?.[`${index}`]?.end_date} />
              </GridRow>
              <GridRow>
                <Input value={edu.degree} label="Degree (e.g. Master, Bachelor)" width="100%"
                  onChange={(v) => handleInputChange(v, index, "degree")} error={educationError?.[`${index}`]?.degree} />
                <Input value={edu.field_of_study} label="Field of Study" width="100%"
                  onChange={(v) => handleInputChange(v, index, "field_of_study")} error={educationError?.[`${index}`]?.field_of_study} />
              </GridRow>
              <GridRow>
                <Input value={edu.country} label="Country" width="100%"
                  onChange={(v) => handleInputChange(v, index, "country")} error={educationError?.[`${index}`]?.country} />
              </GridRow>
            </FormSection>
          </AccordionTab>
        ))}
      </Accordion>

      <ActionRow>
        {onBack && <Button label="Back" icon="pi pi-arrow-left" severity="secondary" onClick={onBack} disabled={loading} />}
        {isDirty && <Button label="Save" icon="pi pi-save" loading={loading} disabled={loading} className="p-button-success" onClick={handleSubmitData} />}
      </ActionRow>
    </FormWrapper>
  );
};

export default Education;
